import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { useVersionSkewNotice } from "@/hooks/useVersionSkewNotice";

/** Null-rendering mount point for the stale-tab detector (needs Sonner in scope). */
const VersionSkewWatcher = () => {
  useVersionSkewNotice();
  return null;
};
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { resetCacheOnOrgChange } from "@/lib/reset-cache-on-org-change";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LocationProvider } from "@/contexts/LocationContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { BatchNotificationProvider } from "@/contexts/BatchNotificationContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import ProductionAccessGuard from "@/components/auth/ProductionAccessGuard";
import NoFloorScreen from "@/components/auth/NoFloorScreen";
import ErrorBoundary from "@/components/ErrorBoundary";
import { addBusinessContext } from "@/lib/sentry";
import { redactSensitive } from "@/lib/telemetry";
import { isExpectedTrpcError } from "@/lib/trpc-error-utils";
import { breadcrumbInsteadOfReport } from "@/lib/query-error-meta";
import { TRPCProvider } from "@/lib/trpc-provider";
import RoleGuard from "@/components/auth/RoleGuard";
import * as Sentry from "@sentry/react";
import { reportDebug, reportWarning } from "@/lib/logging";
import Inventory from "./pages/Inventory";
const WarehousePositions = React.lazy(() => import("./pages/WarehousePositions"));
const ClientMessagesPage = React.lazy(() => import("@/features/order-messages/ClientMessagesPage"));
import InventoryReconciliation from "./pages/InventoryReconciliation";
import Stocktakes from "./pages/Stocktakes";
import StockSuggestions from "./pages/StockSuggestions";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import AcceptOrgInvite from "./pages/AcceptOrgInvite";
import NotFound from "./pages/NotFound";
import Logs from "./pages/Logs";
import Reporting from "./pages/Reporting";
const BlankConsumptionReport = React.lazy(() => import("@/pages/BlankConsumptionReport"));
import SKURules from "./pages/SKURules";
import NotificationsPage from "./pages/Notifications";
import OrdersRouteSwitch from "./pages/OrdersRouteSwitch";
import OrdersList from "./pages/OrdersList";
import HomeRouteSwitch from "./pages/HomeRouteSwitch";
import OrderCreate from "./pages/OrderCreate";
import OrderDetail from "./pages/OrderDetail";
import TransfersList from "./pages/TransfersList";
import TransferCreate from "./pages/TransferCreate";
import OrderEdit from "./pages/OrderEdit";
import Suppliers from "./pages/Suppliers";
import SupplierNew from "./pages/SupplierNew";
import SupplierProfile from "./pages/SupplierProfile";
import Consumables from "./pages/Consumables";
import Settings from "./pages/Settings";
import Xero from "./pages/Xero";
import CostOfGoods from "./pages/CostOfGoods";
import StockHoldBack from "./pages/StockHoldBack";
import Products from "./pages/Products";
import ProductSyncPage from "./pages/ProductSyncPage";
import ProductSyncJobPage from "./pages/ProductSyncJobPage";
import OnboardingPage from "./pages/OnboardingPage";
import ProductDetail from "./pages/ProductDetail";
import ProductDev from "./pages/ProductDev";
import ProductDevDetail from "./pages/ProductDevDetail";
import Shipments from "./pages/Shipments";
import ShipmentCreate from "./pages/ShipmentCreate";
import ShipmentDetail from "./pages/ShipmentDetail";
import ProductionDashboard from "./pages/production/Dashboard";
import ProductionOrders from "./pages/production/Orders";
import ProductionPicker from "./pages/production/Picker";
import { ProductionLayout } from "@/components/layout/ProductionLayout";
import { AppLayout } from "@/components/layout/AppLayout";
import { StudioSessionProvider } from "@/features/upload-studio/session/StudioSessionProvider";
import { BatchDetailScreen, BatchesListPage, ProductionCentreGate } from "@/features/production-orders";
import { CatcherPage } from "@/features/catcher-station";
import { PrintStationPage } from "@/features/print-station";
import { FilmPrintPage } from "@/features/film-print";
import { FilmPreprintPage } from "@/features/film-preprint";
import { PressingPage } from "@/features/pressing";
import { MatchingPage } from "@/features/matching";
import { FilmStoragePage } from "@/features/film-storage";
import { PretreatPage } from "@/features/pretreat-station";
import { ProductionSettingsPage } from "@/features/production-settings";
import { BinDashboardPage, BinManagePage, BenchManagePage, TVModePage } from "@/features/bin-dashboard";
import { PackingPage } from "@/features/packing-station";
import { IssuesPage, OrderResolutionFlow } from "@/features/production-issues";
import { PRODUCTION_SETTINGS_ROLES, RESOLVE_BOARD_ROLES } from "@/lib/roles";
import { ProductionTrackerPage } from "@/features/production-tracker";
import { QAReportsPage, ReportsTVPage } from "@/features/qa-reports";

// Phase 104: manual order page, create and edit (lazy loaded; orders.create holders only).
const ManualOrderPage = React.lazy(() => import("@/pages/production/ManualOrderPage"));
// Phase 91 Upload Studio canvas (reached from a Large cell's "Place on platen").
const UploadStudioCanvasPage = React.lazy(() => import("@/pages/UploadStudioCanvasPage"));

// Phase 34: Pre-Print page (lazy loaded)
const PrePrintPage = React.lazy(() => import("@/pages/production/PrePrintPage"));

// MZ bridge control panel (Phase 2/3) — lazy loaded; the telemetry dashboard +
// @tanstack/react-virtual stay out of the critical production bundle.
const BridgeControlPage = React.lazy(() =>
  import("@/features/bridge-control").then((m) => ({ default: m.BridgeControlPage })),
);

// Partner portal — supplier-org view of a buyer's shared POs + product dev.
// Lazy loaded: most orgs have no partnership, so the bundle stays out of the
// critical path. Routes render nothing useful without an active junction
// (the backend partnerProcedure 403s), and the sidebar entry is gated on
// supplierPortal.getStatus.
const IncomingOrders = React.lazy(() => import("@/pages/partner/IncomingOrders"));
const IncomingOrderDetail = React.lazy(() => import("@/pages/partner/IncomingOrderDetail"));
const SharedProductDev = React.lazy(() => import("@/pages/partner/SharedProductDev"));
const SharedProductDevDetail = React.lazy(() => import("@/pages/partner/SharedProductDevDetail"));

import { LoadingShell } from "@/components/loading/LoadingShell";

// Bar only, no typing: a navigation may hand off from this fallback to a
// page-level PageLoader, and two typewriters restarting in sequence reads
// as a stutter (one typewriter per navigation).
const lazyFallback = <LoadingShell />;
import { HelpRecorderProvider } from "@/features/help/recorder/HelpRecorderContext";
import { RecorderOverlay } from "@/features/help/recorder/RecorderOverlay";
const HelpCenterPage = React.lazy(() => import("@/features/help/HelpCenterPage"));
const HelpRecorderPage = React.lazy(() => import("@/features/help/HelpRecorderPage"));
// Public Partner API docs (no login). Lazy so the OpenAPI contract stays out of the main bundle.
const DevelopersPage = React.lazy(() => import("@/features/developers/DevelopersPage"));
import { StockSyncPage } from "@/features/stock-sync";
import Organizations from "./pages/platform/Organizations";
import OrganizationDetail from "./pages/platform/OrganizationDetail";
import CreateStore from "./pages/platform/CreateStore";
// Platform-only page: lazy, so the catalog CSV template stays out of the main bundle.
const SupplierCatalogPage = React.lazy(() => import("./pages/platform/SupplierCatalog"));
// Reached only from a supplier linked to the catalog: lazy, so the catalog
// browser stays out of the main bundle.
const CatalogSupplier = React.lazy(() => import("./pages/CatalogSupplier"));

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Log query errors to console for development
      if (process.env.NODE_ENV === 'development') {
        reportDebug('Query error', {
          error: error instanceof Error ? error.message : String(error),
          queryKey: query.queryKey,
        });
      }
      
      // By-design tRPC guards (non-500 codes: validation, auth expiry,
      // preconditions) are breadcrumbs, not Sentry errors — they were burying
      // real regressions. Errors WITHOUT a tRPC code (raw network failures)
      // and INTERNAL_SERVER_ERROR (e.g. Supabase 522) are still captured.
      if (isExpectedTrpcError(error)) {
        Sentry.addBreadcrumb({
          category: 'trpc.guard',
          level: 'info',
          message: error instanceof Error ? error.message : String(error),
          data: { queryKey: JSON.stringify(query.queryKey) },
        });
        return;
      }

      // A fast poll that opted out (meta.skipGlobalErrorReport, e.g. the
      // Create Batch status every 2 s): a breadcrumb, never a Sentry error.
      if (breadcrumbInsteadOfReport(error, query)) return;

      // Capture query errors in Sentry
      try {
        Sentry.withScope((scope) => {
          scope.setContext('query', {
            error: error instanceof Error ? error.message : String(error),
            queryKey: query.queryKey,
            queryHash: query.queryHash
          });
          scope.setTag('errorType', 'query');
          scope.setLevel('error');
          
          addBusinessContext({
            operation: 'query',
            queryKey: query.queryKey
          });
          
          Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
        });
      } catch (sentryError) {
        if (process.env.NODE_ENV === 'development') {
          reportWarning('⚠️ Failed to capture query error in Sentry', {
            error: sentryError instanceof Error ? sentryError.message : String(sentryError),
          });
        }
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      const safeVariables = redactSensitive(variables);
      const safeContext = redactSensitive(context);
      // Log mutation errors to console for development
      if (process.env.NODE_ENV === 'development') {
        reportDebug('Mutation error', {
          error: error instanceof Error ? error.message : String(error),
          variables: safeVariables,
          context: safeContext,
        });
      }
      
      // Same guard-vs-error split as the QueryCache above — keep in lockstep.
      if (isExpectedTrpcError(error)) {
        Sentry.addBreadcrumb({
          category: 'trpc.guard',
          level: 'info',
          message: error instanceof Error ? error.message : String(error),
          data: { mutationKey: JSON.stringify(mutation.options.mutationKey ?? null) },
        });
        return;
      }

      // Capture mutation errors in Sentry
      try {
        Sentry.withScope((scope) => {
          scope.setContext('mutation', {
            variables: safeVariables,
            context: safeContext,
            error: error instanceof Error ? error.message : String(error),
            mutationKey: mutation.options.mutationKey
          });
          scope.setTag('errorType', 'mutation');
          scope.setLevel('error');
          
          // Add business context if available
          if (safeVariables && typeof safeVariables === 'object') {
            const businessContext = {
              operation: 'mutation',
              ...(safeVariables as Record<string, unknown>)
            };
            addBusinessContext(businessContext);
          }
          
          Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
        });
      } catch (sentryError) {
        if (process.env.NODE_ENV === 'development') {
          reportWarning('⚠️ Failed to capture mutation error in Sentry', {
            error: sentryError instanceof Error ? sentryError.message : String(sentryError),
          });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors). tRPC v11 client errors
        // carry the status at data.httpStatus, NOT a top-level `status` — the
        // old `'status' in error` check never matched, so every 4xx burned the
        // full 3-retry backoff (~7s of skeletons) before surfacing its error
        // state. (Mirrors the mutations predicate below, which was already
        // correct.)
        const httpStatus =
          (error as { data?: { httpStatus?: number } } | null)?.data?.httpStatus ??
          (error as { status?: number } | null)?.status;
        if (typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500) {
          return false;
        }
        // Retry up to 3 times for server errors
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry once on server/network errors, but NEVER on 4xx. A 4xx includes
      // the BLU-530 409 CONFLICT raised when a status transition lost the
      // idempotency CAS — retrying it would just 409 again and surface a
      // spurious error. (Mirrors the queries predicate above.)
      retry: (failureCount, error) => {
        const httpStatus =
          (error as { data?: { httpStatus?: number } } | null)?.data?.httpStatus ??
          (error as { status?: number } | null)?.status;
        if (typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500) {
          return false;
        }
        return failureCount < 1;
      },
      // Error handling moved to MutationCache above
    },
  },
});

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      {/* BLU-403: physically drop every cached query on org switch so
          cross-org-sensitive hooks (e.g. useConnectedClients) cannot
          flash the prior profile's data while their refetch is in flight. */}
      {(() => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          const handleOrgChange = () => resetCacheOnOrgChange(queryClient);
          window.addEventListener('org-changed', handleOrgChange);
          return () => window.removeEventListener('org-changed', handleOrgChange);
        }, []);
        return null;
      })()}
      <TRPCProvider queryClient={queryClient}>
        <TooltipProvider delayDuration={120} skipDelayDuration={0}>
          <BrowserRouter>
            <AuthProvider>
            <LanguageProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />
              <Route path="/accept-org-invite" element={<AcceptOrgInvite />} />
              <Route path="/developers" element={<React.Suspense fallback={lazyFallback}><DevelopersPage /></React.Suspense>} />
              <Route
                path="/*"
                element={
                  <OrganizationProvider>
                    <LocationProvider>
                      <WorkspaceProvider>
                        <Toaster />
                        <Sonner />
                        <VersionSkewWatcher />
                        {/* BlueNote global recorder: provider ABOVE the routed
                            pages so a recording survives navigation; the overlay
                            pill renders on every route while recording. */}
                        <HelpRecorderProvider>
                        <RecorderOverlay />
                        {/* Upload Studio: the save queue and uploads keep going after
                            the studio page is left, until every save is sent. */}
                        <StudioSessionProvider>
                        <ErrorBoundary>
                          <Routes>
                        {/* AppLayout wraps all authenticated routes with sidebar + top header */}
                        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                          {/* BLU-666: a pure 3PL client lands on their order
                              inbox, not the stock dashboard — the switch lives in
                              HomeRouteSwitch so owner/hybrid orgs keep '/' as the
                              Inventory dashboard, unchanged. */}
                          <Route path="/" element={<HomeRouteSwitch />} />
                          {/* Explicit path for the stock dashboard: '/' redirects
                              pure clients away, so their sidebar's Inventory item
                              needs a route that always renders it. Owner orgs can
                              use either path. */}
                          <Route path="/inventory" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'Viewer', 'CX']}>
                              <Inventory />
                            </RoleGuard>
                          } />
                          {/* BLU-711 A2: stocktake proposals awaiting approval —
                              it corrects stock, so it's reserved for the same
                              tier as Purchase Orders/Transfers/Product Dev
                              (Admin/Coordinator, Viewer read-only), NOT
                              Warehouse Staff — mirrors /product-dev's role
                              list exactly. The write actions inside are
                              additionally gated by the backend's client-org +
                              Admin/Coordinator check (RBAC layer 1 of 4). */}
                          <Route path="/inventory/positions" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'Viewer', 'CX']}>
                              <React.Suspense fallback={null}><WarehousePositions /></React.Suspense>
                            </RoleGuard>
                          } />
                          <Route path="/inventory/stocktakes" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Viewer']}>
                              <Stocktakes />
                            </RoleGuard>
                          } />
                          <Route path="/inventory/reconciliation" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                              <InventoryReconciliation />
                            </RoleGuard>
                          } />
                          {/* Receive History was promoted into the Logs page as
                              a tab — keep this URL alive as a redirect so
                              existing bookmarks still land in the right place. */}
                          <Route path="/inventory/receipts" element={
                            <Navigate to="/logs?tab=receive_history" replace />
                          } />
                          <Route path="/products" element={<Products />} />
                          <Route path="/onboarding" element={<OnboardingPage />} />
                          <Route path="/product-sync" element={<ProductSyncPage />} />
                          <Route path="/product-sync/jobs/:jobId" element={<ProductSyncJobPage />} />
                          <Route path="/upload-studio/:productId" element={
                            <RoleGuard requirePermission="org_settings.read">
                              <React.Suspense fallback={null}><UploadStudioCanvasPage /></React.Suspense>
                            </RoleGuard>
                          } />
                          <Route path="/products/:productId" element={<ProductDetail />} />
                          <Route path="/product-dev" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Viewer']}>
                              <ProductDev />
                            </RoleGuard>
                          } />
                          <Route path="/product-dev/:id" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Viewer']}>
                              <ProductDevDetail />
                            </RoleGuard>
                          } />
                          <Route path="/stock-alerts" element={<StockSuggestions />} />
                          {/* Redirect old route */}
                          <Route path="/inventory/stock-suggestions" element={<Navigate to="/stock-alerts" replace />} />
                          {/* BLU-660: pure 3PL clients (no owned location) get the
                              read-only client order dashboard here; owner/hybrid
                              orgs keep the Purchase Orders list. Switch lives in
                              OrdersRouteSwitch so OrdersList stays untouched. */}
                          <Route path="/orders" element={<OrdersRouteSwitch />} />
                          {/* BLU-666: clients DO run a Purchase Orders workflow (they
                              buy their own stock), but '/orders' is their order inbox —
                              so the PO list also answers at '/purchase-orders', which is
                              what the client sidebar links to. Owner/hybrid orgs keep
                              reaching the same list at '/orders'; nothing changes for
                              them. */}
                          <Route path="/purchase-orders" element={<OrdersList />} />
                          {/* PO create/detail/edit are shared by both org shapes — a
                              client reaches them from /purchase-orders. (Previously
                              ProductionAccessGuard bounced pure clients away, when they
                              had no PO workflow at all.) */}
                          <Route path="/orders/new" element={<OrderCreate />} />
                          <Route path="/orders/:id" element={<OrderDetail />} />
                          <Route path="/orders/:id/edit" element={<OrderEdit />} />
                          {/* BLU-569: transfers get their own list + create page.
                              Detail reuses the transfer-aware OrderDetail. A
                              transfer opened at /orders/:id redirects here so
                              transfer URLs converge on /transfers/:id. */}
                          <Route path="/transfers" element={<TransfersList />} />
                          <Route path="/transfers/new" element={<TransferCreate />} />
                          <Route path="/transfers/:id" element={<OrderDetail />} />
                          <Route path="/shipments" element={<Shipments />} />
                          <Route path="/shipments/new" element={<ShipmentCreate />} />
                          <Route path="/shipments/:id" element={<ShipmentDetail />} />
                          <Route path="/logs" element={<Logs />} />
                          {/* BLU-1268: the page gate matches the sidebar item and the routers (suppliers.view). */}
                          <Route path="/suppliers" element={
                            <RoleGuard requirePermission="suppliers.view">
                              <Suppliers />
                            </RoleGuard>
                          } />
                          <Route path="/suppliers/new" element={
                            <RoleGuard requirePermission="suppliers.view">
                              <SupplierNew />
                            </RoleGuard>
                          } />
                          <Route path="/suppliers/catalog/:catalogSupplierId" element={
                            <RoleGuard requirePermission="suppliers.view">
                              <React.Suspense fallback={lazyFallback}><CatalogSupplier /></React.Suspense>
                            </RoleGuard>
                          } />
                          <Route path="/suppliers/:id" element={
                            <RoleGuard requirePermission="suppliers.view">
                              <SupplierProfile />
                            </RoleGuard>
                          } />
                          <Route path="/consumables" element={<Consumables />} />
                          <Route path="/reporting" element={<Reporting />} />
                          <Route path="/reporting/blanks" element={
                            <React.Suspense fallback={lazyFallback}>
                              <BlankConsumptionReport />
                            </React.Suspense>
                          } />
                          <Route path="/notifications" element={<NotificationsPage />} />
                          <Route path="/rules" element={<SKURules />} />
                          <Route path="/cost-of-goods" element={<CostOfGoods />} />
                          <Route path="/stock-hold-back" element={<StockHoldBack />} />
                          {/* Redirect old route */}
                          <Route path="/category-costs" element={<Navigate to="/cost-of-goods" replace />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/xero" element={<Xero />} />
                          <Route path="/help" element={<React.Suspense fallback={lazyFallback}><HelpCenterPage /></React.Suspense>} />
                          <Route path="/help/manage" element={
                            <RoleGuard requirePlatformAdmin>
                              <React.Suspense fallback={lazyFallback}><HelpRecorderPage /></React.Suspense>
                            </RoleGuard>
                          } />

                          {/* Partner portal (supplier-org side) — nav gated on
                              supplierPortal.getStatus; data gated per-request by
                              the backend junction check. */}
                          <Route path="/partner/orders" element={
                            <React.Suspense fallback={lazyFallback}>
                              <IncomingOrders />
                            </React.Suspense>
                          } />
                          <Route path="/partner/orders/:id" element={
                            <React.Suspense fallback={lazyFallback}>
                              <IncomingOrderDetail />
                            </React.Suspense>
                          } />
                          <Route path="/partner/product-dev" element={
                            <React.Suspense fallback={lazyFallback}>
                              <SharedProductDev />
                            </React.Suspense>
                          } />
                          <Route path="/partner/product-dev/:id" element={
                            <React.Suspense fallback={lazyFallback}>
                              <SharedProductDevDetail />
                            </React.Suspense>
                          } />
                          <Route path="/stock-sync" element={
                            <RoleGuard allowedRoles={['Super Admin', 'Admin']}>
                              <StockSyncPage />
                            </RoleGuard>
                          } />

                          {/* Platform management routes (platform admin list only,
                              BLU-1313; never the 'Super Admin' role) */}
                          <Route path="/platform/stores" element={
                            <RoleGuard requirePlatformAdmin>
                              <Organizations />
                            </RoleGuard>
                          } />
                          <Route path="/platform/stores/new" element={
                            <RoleGuard requirePlatformAdmin>
                              <CreateStore />
                            </RoleGuard>
                          } />
                          <Route path="/platform/stores/:id" element={
                            <RoleGuard requirePlatformAdmin>
                              <OrganizationDetail />
                            </RoleGuard>
                          } />
                          <Route path="/platform/catalog" element={
                            <RoleGuard requirePlatformAdmin>
                              <React.Suspense fallback={lazyFallback}><SupplierCatalogPage /></React.Suspense>
                            </RoleGuard>
                          } />
                          {/* Redirects from old routes */}
                          <Route path="/platform/organizations" element={<Navigate to="/platform/stores" replace />} />
                          <Route path="/platform/organizations/:id" element={<Navigate to="/platform/stores" replace />} />

                          {/* Production workspace routes — BLU-658: ProductionAccessGuard
                              redirects a pure 3PL client (owns no location) to /orders,
                              the URL-typing backstop for the sidebar toggle's hide/auto-pin. */}
                          <Route path="/production" element={
                            <ProductionAccessGuard>
                              <BatchNotificationProvider>
                                <ProductionLayout />
                              </BatchNotificationProvider>
                            </ProductionAccessGuard>
                          }>
                            <Route index element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionDashboard />
                              </RoleGuard>
                            } />
                            <Route path="dashboard" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionDashboard />
                              </RoleGuard>
                            } />
                            <Route path="orders" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <ProductionOrders />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* Phase 104 (D-01, D-23): roles = the orders.create holders. */}
                            <Route path="orders/new" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <React.Suspense fallback={null}>
                                    <ManualOrderPage />
                                  </React.Suspense>
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* Phase 104 (D-01, D-21): edit a manual order until it is batched; same guard. */}
                            <Route path="orders/manual/:id" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <React.Suspense fallback={null}>
                                    <ManualOrderPage />
                                  </React.Suspense>
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* BLU-1295 owner Client messages inbox: orders.view role set,
                                deliberately NOT inside ProductionCentreGate (it spans every
                                location the owner runs). */}
                            <Route path="client-messages" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'Viewer', 'CX']}>
                                <React.Suspense fallback={null}>
                                  <ClientMessagesPage />
                                </React.Suspense>
                              </RoleGuard>
                            } />
                            <Route path="tracker" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'Picker', 'Printer', 'Catcher', 'Packer', 'CX']}>
                                <ProductionCentreGate>
                                  <ProductionTrackerPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="picker" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Picker', 'Printer']}>
                                <ProductionCentreGate>
                                  <ProductionPicker />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="pretreat" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <PretreatPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="printer" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Printer']}>
                                <ProductionCentreGate>
                                  <PrintStationPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* Phase 50.01.3 / BLU-233: DTF film-print station — same
                                role set as the DTG print station (Printer + coordinator),
                                plus the film-only DTF Printer. */}
                            <Route path="film-printing" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Printer', 'DTF Printer']}>
                                <ProductionCentreGate>
                                  <FilmPrintPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* PRE-PRINTED DTF film stock. Coordinators create and
                                release pre-print batches; DTF Printers print and rack
                                the films (backend: the pre-print print-and-rack tier). */}
                            <Route path="film-preprint" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'DTF Printer']}>
                                <ProductionCentreGate>
                                  <FilmPreprintPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* Phase 50.01.4 / BLU-233: DTF heat press station —
                                mirrors the catcher role set (film↔garment convergence /
                                press QC). */}
                            <Route path="pressing" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Catcher']}>
                                <ProductionCentreGate>
                                  <PressingPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* DTF Matching station (Concept B): pull the film from
                                its hole + scan-confirm the pairing. Packer (runs film
                                storage) + Catcher (did the match at press) both allowed. */}
                            <Route path="matching" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Packer', 'Catcher']}>
                                <ProductionCentreGate>
                                  <MatchingPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="catcher" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Catcher']}>
                                <ProductionCentreGate>
                                  <CatcherPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="bins" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Packer']}>
                                <ProductionCentreGate>
                                  <BinDashboardPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* Phase 50.01.4 / BLU-233: DTF film storage dashboard —
                                Class B owner-side physical, mirrors bins (Packer +
                                coordinator), plus the DTF Printer who racks films. */}
                            <Route path="film-storage" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Packer', 'DTF Printer']}>
                                <ProductionCentreGate>
                                  <FilmStoragePage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="bins/manage" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Packer']}>
                                <ProductionCentreGate>
                                  <BinManagePage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* BLU-197: work-bench management — coordinator-only (mirrors bins/manage). */}
                            <Route path="benches/manage" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <BenchManagePage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* bins/tv route moved outside AppLayout for chrome-free display */}
                            <Route path="packer" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Packer']}>
                                <ProductionCentreGate>
                                  <PackingPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="issues" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'CX']}>
                                <ProductionCentreGate>
                                  <IssuesPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            {/* BLU-563: split-order RESOLVER (assignment board) — routed
                                (NOT a modal on the OrderDetailModal Sheet, DESIGN.md §10).
                                Same layout + guard as the sibling /production/issues
                                surface. Keyed off a prod_order id; the server expands it
                                to the whole order. Supersedes the retired one-shot
                                SplitLocationFlow routes (issues/split/...). */}
                            <Route path="issues/resolve/:prodOrderId" element={
                              <RoleGuard allowedRoles={[...RESOLVE_BOARD_ROLES]}>
                                <OrderResolutionFlow />
                              </RoleGuard>
                            } />
                            <Route path="reports" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <QAReportsPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="batches" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Picker', 'Printer']}>
                                <ProductionCentreGate>
                                  <BatchesListPage />
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="batches/:batchId" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator', 'Picker', 'Printer']}>
                                <BatchDetailScreen />
                              </RoleGuard>
                            } />
                            <Route path="settings" element={
                              <RoleGuard allowedRoles={[...PRODUCTION_SETTINGS_ROLES]}>
                                <ProductionSettingsPage />
                              </RoleGuard>
                            } />
                            <Route path="pre-print" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <ProductionCentreGate>
                                  <React.Suspense fallback={lazyFallback}>
                                    <PrePrintPage />
                                  </React.Suspense>
                                </ProductionCentreGate>
                              </RoleGuard>
                            } />
                            <Route path="bridge" element={
                              <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                                <React.Suspense fallback={lazyFallback}>
                                  <BridgeControlPage />
                                </React.Suspense>
                              </RoleGuard>
                            } />
                            <Route path="help" element={<Navigate to="/help" replace />} />
                            <Route path="batches/new" element={<Navigate to="/production/orders" replace />} />
                          </Route>
                        </Route>

                        {/* DTF Printer with no floor of its own: outside AppLayout (no
                            sidebar) and outside ProductionAccessGuard, so nothing
                            bounces it back to a page it cannot use. */}
                        <Route path="/no-floor" element={<ProtectedRoute><NoFloorScreen /></ProtectedRoute>} />

                        {/* TV Mode - outside AppLayout for no sidebar/header */}
                        <Route path="/production/bins/tv" element={
                          <ProtectedRoute>
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                              <ProductionAccessGuard>
                                <TVModePage />
                              </ProductionAccessGuard>
                            </RoleGuard>
                          </ProtectedRoute>
                        } />
                        <Route path="/production/reports/tv" element={
                          <ProtectedRoute>
                            <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
                              <ProductionAccessGuard>
                                <ReportsTVPage />
                              </ProductionAccessGuard>
                            </RoleGuard>
                          </ProtectedRoute>
                        } />

                        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                        <Route path="*" element={<NotFound />} />
                          </Routes>
                        </ErrorBoundary>
                        </StudioSessionProvider>
                        </HelpRecorderProvider>
                      </WorkspaceProvider>
                    </LocationProvider>
                  </OrganizationProvider>
                }
              />
            </Routes>
          </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
      </TRPCProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
